import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  Loader2,
  ArrowRight,
  ShieldCheck,
  Eye,
  EyeOff,
  Lock,
  User2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import LoginOrbital from "@/components/LoginOrbital";

/*
 * Login page — visual layer only.
 * All form state, validation and `useAuth().login()` call are unchanged.
 */
export default function LoginPage() {
  const { isAuthed, login } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (error) setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, password]);

  if (isAuthed) {
    const from = location.state?.from?.pathname;
    const redirect = from && from !== "/change-password" ? from : "/dashboard";
    return <Navigate to={redirect} replace />;
  }

  const canSubmit = !!username.trim() && !!password && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full bg-white text-[#1a1a2e] overflow-hidden"
      data-testid="login-page"
    >
      <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-3">
        {/* Left 2/3 — orbital ecosystem (hidden below 768px, replaced by
            static miniature above the card) */}
        <div className="hidden md:block lg:col-span-2 relative">
          <LoginOrbital />
        </div>

        {/* Right 1/3 — login card */}
        <div
          className="relative flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8 lg:py-0"
          style={{ background: "#ffffff" }}
        >
          <div
            className="w-full max-w-[380px]"
            style={{ animation: "login-card-slide-in 0.5s ease-out both" }}
          >
            <div
              className="p-7 sm:p-8"
              style={{
                background: "#ffffff",
                border: "1px solid #e8eaf0",
                borderRadius: 16,
                boxShadow: "0 8px 40px rgba(28,43,74,0.10)",
              }}
            >
              <div className="flex flex-col items-center text-center mb-6">
                <img
                  src="https://customer-assets-jai6qajn.emergentagent.net/wingman/d52fcc99-6323-4175-a4bb-945511635e75/attachments/948677adb4084671bd935741a72dd65c_NDM%20LOGO.jpeg"
                  alt="NDM Advisors"
                  width={72}
                  height={72}
                  className="rounded-md object-contain select-none"
                  draggable={false}
                  style={{ width: 72, height: "auto" }}
                />
                <h1
                  className="mt-3"
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#1a1a2e",
                    letterSpacing: "-0.01em",
                  }}
                >
                  NDM Advisors
                </h1>
                <p
                  style={{
                    fontSize: 13,
                    color: "#888888",
                    marginTop: 2,
                  }}
                >
                  Compliance Dashboard
                </p>
                <div
                  aria-hidden
                  style={{
                    height: 2,
                    width: 40,
                    background: "#b8860b",
                    borderRadius: 999,
                    margin: "10px auto 8px",
                  }}
                />
                <p
                  style={{
                    fontSize: 12,
                    color: "#6b6bb5",
                    letterSpacing: "0.04em",
                  }}
                >
                  Restricted Access
                </p>
              </div>

              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5">
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      color: "#1a1a2e",
                      textTransform: "uppercase",
                    }}
                  >
                    User ID
                  </span>
                  <div className="relative">
                    <User2
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                      style={{ color: "#6b6bb5" }}
                      aria-hidden
                    />
                    <input
                      autoFocus
                      autoComplete="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="admin"
                      data-testid="login-username"
                      className="ndm-input w-full h-11 pl-9 pr-3 rounded-md text-sm outline-none transition-colors"
                      style={{
                        background: "#f8f9fc",
                        border: "1px solid #dde2f0",
                        color: "#1a1a2e",
                      }}
                    />
                  </div>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      color: "#1a1a2e",
                      textTransform: "uppercase",
                    }}
                  >
                    Password
                  </span>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                      style={{ color: "#6b6bb5" }}
                      aria-hidden
                    />
                    <input
                      type={showPw ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      data-testid="login-password"
                      className="ndm-input w-full h-11 pl-9 pr-11 rounded-md text-sm outline-none transition-colors"
                      style={{
                        background: "#f8f9fc",
                        border: "1px solid #dde2f0",
                        color: "#1a1a2e",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      data-testid="login-toggle-password"
                      style={{ color: "#6b6bb5" }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f2f8")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </label>

                {error && (
                  <div
                    role="alert"
                    data-testid="login-error"
                    className="text-xs px-3 py-2 rounded-md"
                    style={{
                      color: "#c0392b",
                      background: "#fdecec",
                      border: "1px solid #f5c9c4",
                    }}
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="login-submit"
                  className={cn(
                    "relative w-full h-11 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2 transition-all",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                  style={{
                    background: "#1a1a2e",
                    color: "#ffffff",
                    borderRadius: 12,
                    boxShadow:
                      "0 4px 14px rgba(26,26,46,0.24), inset 0 1px 0 rgba(255,255,255,0.08)",
                  }}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <div
                  className="flex items-center gap-1.5 justify-center mt-1"
                  style={{ color: "#aaaaaa", fontSize: 11 }}
                >
                  <ShieldCheck className="h-3 w-3" />
                  Sessions expire automatically after 12 hours of inactivity.
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile miniature preview (<768px) — static, non-animated */}
      <div className="md:hidden fixed inset-x-0 top-0 pointer-events-none" aria-hidden>
        <div
          className="mx-auto mt-4 mb-2 flex flex-col items-center"
          style={{ opacity: 0.9 }}
        >
          <div
            className="rounded-full"
            style={{
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), #f0f2f8 60%, rgba(107,107,181,0.15))",
              border: "1px solid #b8860b",
              boxShadow: "inset 0 0 30px rgba(107,107,181,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1a1a2e",
              fontWeight: 700,
              fontSize: 20,
              letterSpacing: "-0.02em",
            }}
          >
            NDM
          </div>
        </div>
      </div>

      <style>{`
        @keyframes login-card-slide-in {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes login-node-in {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        .ndm-input:focus {
          border-color: #6b6bb5 !important;
          background: #ffffff !important;
        }
        .ndm-input::placeholder {
          color: #b6bccd;
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="login-page"] * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}
